<script setup lang="ts">
import './CallTimesSettingModal.css';
import { onMounted, ref, computed } from 'vue';
import { closeModal, updateShow } from '@/app/components/modal/modals';
import type { ModalProps } from '@/app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DtCheckbox } from '@dialpad/dialtone/vue3/lib/checkbox';
import { DtTooltip } from '@dialpad/dialtone/vue3/lib/tooltip';
import { DtValidationMessages } from '@dialpad/dialtone/vue3/lib/validation-messages';
import { DtIcon } from '@dialpad/dialtone/vue3/lib/icon';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import { DATA_QA } from './call-times-setting-modal.constants';
import logger from '@/logger';
import { format } from 'date-fns';
import { useCampaignSettingsStore } from '@/stores';
import TimeSelect from '../../TimeSelect/TimeSelect.vue';
import { useI18N } from '@dialpad/i18n';
import {
  createBaseDate,
  createStartOfDay,
  createEndOfDay,
  formatTime,
  stringToDate,
  dateToString,
  compareTimeStrings,
  getMinEndTime,
} from './time-utils';
import { useTimeRangeValidation } from './use-time-range-validation';
import type { TimeRange } from './types';
import { DaysOfWeek } from '../CallTimesSetting/types';

const { $t } = useI18N();

const log = logger.getChild('CallTimesSettingsModal');
log.debug('Mounting');
const props = defineProps<ModalProps>();

const campaignSettingsStore = useCampaignSettingsStore();
const campaignSettings = campaignSettingsStore.watch(
  () => props.meta?.id ?? null,
  'CallTimesSettingModal',
);

const baseDate = createBaseDate();
const startOfDay = createStartOfDay(baseDate);
const endOfDay = createEndOfDay(baseDate);

const { hasErrors, errorMessage, validateTimeRanges } =
  useTimeRangeValidation();

const mounted = ref(false);
const show = ref(true);
const selectedDay = ref<DaysOfWeek>(DaysOfWeek.MONDAY);
const timeRanges = ref<TimeRange[]>([]);
const applyToDays = ref<Record<DaysOfWeek, boolean>>({
  [DaysOfWeek.MONDAY]: false,
  [DaysOfWeek.TUESDAY]: false,
  [DaysOfWeek.WEDNESDAY]: false,
  [DaysOfWeek.THURSDAY]: false,
  [DaysOfWeek.FRIDAY]: false,
  [DaysOfWeek.SATURDAY]: false,
  [DaysOfWeek.SUNDAY]: false,
});

const loading = ref(true);

const fetchCampaignSettings = async () => {
  if (campaignSettings.value.item ?? !props.meta?.id) return;
  try {
    await campaignSettingsStore.read(props.meta.id);
  } catch (error) {
    log.error('Failed to read campaign settings', error);
    void closeModal();
  }
};

const setTimeRangesForSelectedDay = () => {
  const dayTimeRanges =
    campaignSettings.value.item?.timegateSettings?.hours?.[selectedDay.value];

  timeRanges.value =
    dayTimeRanges && dayTimeRanges.length > 0
      ? JSON.parse(JSON.stringify(dayTimeRanges))
      : [['08:00', '21:00']];
};

onMounted(async () => {
  log.debug('Mounted');
  mounted.value = true;
  if (!props.meta?.id || !props.meta.currentDay) {
    log.error('Missing campaign settings ID or currentDay in modal meta');
    void closeModal();
    return;
  }
  const validDays: string[] = Object.values(DaysOfWeek);
  selectedDay.value =
    props.meta.currentDay && validDays.includes(props.meta.currentDay)
      ? (props.meta.currentDay as DaysOfWeek)
      : DaysOfWeek.MONDAY;

  await fetchCampaignSettings();
  setTimeRangesForSelectedDay();
  applyToDays.value[selectedDay.value] = true;
  loading.value = false;
});

const sortedTimeRanges = computed(() => {
  return [...timeRanges.value].sort((a, b) => compareTimeStrings(a[0], b[0]));
});

const isAddHoursDisabled = computed(() => {
  if (timeRanges.value.length === 0) return false;
  const lastRange = sortedTimeRanges.value.at(-1);
  return lastRange ? ['24:00', '00:00'].includes(lastRange[1]) : false;
});

// eslint-disable-next-line max-statements
const addTimeRange = () => {
  if (timeRanges.value.length === 0) {
    timeRanges.value.push(['08:00', '21:00']);
    validateTimeRanges(timeRanges.value);
    return;
  }

  const lastRange = sortedTimeRanges.value.at(-1);
  if (!lastRange) {
    return;
  }
  const latestEnd = lastRange[1];

  if (latestEnd === '00:00') return;

  const latestEndDate = stringToDate(latestEnd, baseDate);
  const newStartDate = new Date(latestEndDate.getTime() + 5 * 60 * 1000);

  if (newStartDate >= endOfDay) return;

  const start = format(newStartDate, 'HH:mm');
  const ninePM = '21:00';
  let end: string;

  if (compareTimeStrings(start, ninePM) < 0) {
    end = ninePM;
  } else {
    const newEndDate = new Date(newStartDate.getTime() + 5 * 60 * 1000);
    end = newEndDate >= endOfDay ? '24:00' : format(newEndDate, 'HH:mm');
  }

  timeRanges.value.push([start, end]);
  validateTimeRanges(timeRanges.value);
};

const removeTimeRange = (rangeToRemove: TimeRange) => {
  const index = timeRanges.value.indexOf(rangeToRemove);
  if (index > -1) {
    timeRanges.value.splice(index, 1);
  }
  validateTimeRanges(timeRanges.value);
};

const updateTimeRange = (
  rangeToUpdate: TimeRange,
  field: 'start' | 'end',
  value: Date | null | undefined,
) => {
  if (!value) return;
  const range = timeRanges.value.find((r) => r === rangeToUpdate);
  if (!range) {
    return;
  }
  const timeString = dateToString(value, baseDate);
  const fieldIndex = field === 'start' ? 0 : 1;
  range[fieldIndex] = timeString;
  if (field === 'start') {
    const startDate = stringToDate(timeString, baseDate);
    const endDate = stringToDate(range[1], baseDate);
    const isEndMidnight = range[1] === '00:00';
    if (!isEndMidnight && startDate >= endDate) {
      const newEndDate = new Date(startDate.getTime() + 30 * 60 * 1000);
      range[1] = newEndDate >= endOfDay ? '24:00' : format(newEndDate, 'HH:mm');
    }
  }
  validateTimeRanges(timeRanges.value);
};

const getSelectedDaysToApply = () => {
  return Object.entries(applyToDays.value)
    .filter(([day, selected]) => selected && day !== selectedDay.value)
    .map(([day]) => day);
};

const getUpdatedHoursAndDays = () => {
  const currentSettings = campaignSettings.value.item;
  if (!currentSettings) {
    log.error('Campaign settings not found for campaign:', props.meta?.id);
    return null;
  }
  const currentHours = currentSettings.timegateSettings?.hours ?? {};

  const hours = { ...currentHours };
  const otherDaysToApply = getSelectedDaysToApply();

  hours[selectedDay.value] = timeRanges.value;
  for (const day of otherDaysToApply) {
    hours[day] = timeRanges.value;
  }
  return { hours, otherDaysToApply };
};

const getDayLabel = (day: string): string => {
  return $t(`CAMPAIGN_EDIT_INTERACTION_WINDOW_WEEKDAY_${day.toUpperCase()}`);
};

const dayColumns = computed(() => [
  [
    DaysOfWeek.MONDAY,
    DaysOfWeek.TUESDAY,
    DaysOfWeek.WEDNESDAY,
    DaysOfWeek.THURSDAY,
  ],
  [DaysOfWeek.FRIDAY, DaysOfWeek.SATURDAY, DaysOfWeek.SUNDAY],
]);

const confirm = () => {
  if (!validateTimeRanges(timeRanges.value)) return;
  if (!props.meta?.id) {
    log.error('Campaign ID is missing');
    return;
  }

  const result = getUpdatedHoursAndDays();
  if (!result) return;
  const { hours, otherDaysToApply } = result;

  try {
    campaignSettingsStore.update(props.meta.id, {
      timegateSettings: {
        hours,
      },
    });
    log.debug('Saving call times settings for campaign:', props.meta.id, {
      day: selectedDay.value,
      timeRanges: timeRanges.value,
      applyToDays: otherDaysToApply,
    });
  } catch (error) {
    log.error('Failed to update timegate settings', error);
  }

  void closeModal();
};

const cancel = () => {
  void closeModal();
};
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('SET_CAMPAIGN_INTERACTION_WINDOW_MODAL_TITLE')"
    :subtitle="
      $t('SET_CAMPAIGN_INTERACTION_WINDOW_MODAL_TITLE_DAY', {
        day: getDayLabel(selectedDay),
      })
    "
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    :data-qa-loading="loading"
    content-class="call-times-setting-modal d-p4 d-mn4"
    @update:show="updateShow"
  >
    <template #header>
      <div class="d-d-flex d-cg8 d-ai-center">
        <h2 class="d-fs-200 d-fw-bold d-fs-normal">
          {{ $t('SET_CAMPAIGN_INTERACTION_WINDOW_MODAL_TITLE') }}
          <span class="d-fw-normal d-fc-black-800 d-tt-capitalize">
            {{
              $t('SET_CAMPAIGN_INTERACTION_WINDOW_MODAL_TITLE_DAY', {
                day: selectedDay,
              })
            }}
          </span>
        </h2>
      </div>
    </template>
    <div
      :data-qa="DATA_QA.ROOT"
      class="d-d-flex d-fd-column d-divide-y d-divide-default d-w100p"
    >
      <div class="d-pb16">
        <div
          v-for="(range, index) in sortedTimeRanges"
          :key="index"
          class="d-d-flex d-fd-column d-ai-center d-cg8 d-mb12"
        >
          <div class="d-d-flex d-fd-row d-cg12 d-w100p d-ai-center">
            <div class="d-w100p">
              <TimeSelect
                :data-qa="`${DATA_QA.TIME_RANGE_START}-${index}`"
                :model-value="stringToDate(range[0], baseDate)"
                :start-on="
                  index === 0
                    ? startOfDay
                    : stringToDate(sortedTimeRanges[index - 1][1], baseDate)
                "
                :end-on="endOfDay"
                :step="5"
                :item-title-format-function="formatTime"
                label=""
                :hide-icon="true"
                @update:model-value="
                  (value: Date | null | undefined) => updateTimeRange(range, 'start', value)
                "
              />
            </div>
            <div>to</div>
            <div class="d-w100p">
              <TimeSelect
                :data-qa="`${DATA_QA.TIME_RANGE_END}-${index}`"
                :model-value="stringToDate(range[1], baseDate)"
                :start-on="getMinEndTime(range[0], baseDate)"
                :end-on="endOfDay"
                :step="5"
                :item-title-format-function="formatTime"
                label=""
                :hide-icon="true"
                @update:model-value="
                  (value: Date | null | undefined) => updateTimeRange(range, 'end', value)
                "
              />
            </div>
            <DtButton
              v-if="timeRanges.length > 1"
              :data-qa="`${DATA_QA.REMOVE_TIME_RANGE_BUTTON}-${index}`"
              importance="clear"
              kind="muted"
              size="xs"
              class="d-mrn8 d"
              :aria-label="'Remove time range'"
              @click="() => removeTimeRange(range)"
            >
              <template #icon>
                <DtIcon name="trash" size="300" />
              </template>
            </DtButton>
          </div>
        </div>
        <DtTooltip
          v-if="isAddHoursDisabled"
          message="No more time available. Edit times above to make room."
          placement="top"
        >
          <template #anchor>
            <span class="d-d-inline-block">
              <DtButton
                :data-qa="DATA_QA.ADD_HOURS_BUTTON"
                class="d-mln8"
                importance="clear"
                size="xs"
                :disabled="true"
                @click="addTimeRange"
              >
                Add hours
              </DtButton>
            </span>
          </template>
        </DtTooltip>
        <DtButton
          v-else
          :data-qa="DATA_QA.ADD_HOURS_BUTTON"
          class="d-mln8"
          importance="clear"
          size="xs"
          @click="addTimeRange"
        >
          Add hours
        </DtButton>

        <DtValidationMessages
          v-if="hasErrors"
          :data-qa="DATA_QA.ERROR_MESSAGE"
          :validation-messages="[{ message: errorMessage, type: 'error' }]"
        />
      </div>
      <div class="d-d-flex d-fd-column d-pt16 d-rg12">
        <p>Select the days you would like these hours to apply to</p>
        <div class="d-d-flex d-fd-row d-rg12">
          <div
            v-for="(column, columnIndex) in dayColumns"
            :key="columnIndex"
            class="d-fl1"
          >
            <template v-for="day in column" :key="day">
              <DtTooltip
                v-if="selectedDay === day"
                message="You are editing hours for this day."
                placement="left"
              >
                <template #anchor>
                  <DtCheckbox
                    :name="day"
                    :data-qa="`${DATA_QA.APPLY_TO_DAY_CHECKBOX}-${day}-disabled`"
                    :label="day"
                    :aria-label="`${day} checkbox`"
                    :checked="true"
                    disabled
                  />
                </template>
              </DtTooltip>
              <DtCheckbox
                v-else
                v-model="applyToDays[day as keyof typeof applyToDays]"
                :name="day"
                :data-qa="`${DATA_QA.APPLY_TO_DAY_CHECKBOX}-${day}`"
                :label="day"
                :aria-label="`${day} checkbox`"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton
        :data-qa="DATA_QA.CANCEL_BUTTON"
        importance="clear"
        @click="cancel"
      >
        {{ $t('SET_CAMPAIGN_INTERACTION_WINDOW_MODAL_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.SAVE_BUTTON"
        importance="primary"
        class="d-ml6"
        @click="confirm"
      >
        {{ $t('SET_CAMPAIGN_INTERACTION_WINDOW_MODAL_SAVE') }}
      </DtButton>
    </div>
  </DtModal>
</template>
